// "Invitar a la prueba" (Backoffice → Plataforma → Contactos): one click
// creates the prospect's REAL workshop (crearTaller + brand + commercial
// record in pilot + example car) and sends "Correo A" of the contacts
// playbook (docs/comercial/proceso-contactos.md) with its temporary password.
// The backoffice prefills the e-mail from the lead and the superadmin may
// edit it; two markers stay for the server: the password (generated there,
// never seen by the browser unless the e-mail fails) and the trial
// conditions, inserted verbatim (legal text: nobody rewrites it per lead).
import { z } from 'zod';
import { esHexColor } from './color.ts';
import { LINEA_IOS, PLAY_URL } from './correoHtml.ts';
import { esDiaIso } from './comercial.ts';
import { validarRut } from './rut.ts';

export const MARCA_CLAVE = '{{clave}}';
export const MARCA_CONDICIONES = '{{condiciones}}';

/** Default trial length (playbook: 30 days from Correo A). */
export const DIAS_PRUEBA = 30;
/** Allowed end of the trial, in days from today (server check). */
export const RANGO_FIN_PRUEBA = { min: 7, max: 120 } as const;
export const MAX_CUERPO_INVITACION = 12_000;
/** Same cap as storage.rules for talleres/{t}/marca (PNG, bytes). */
export const MAX_LOGO_INVITACION = 512 * 1024;
const MAX_LOGO_BASE64 = Math.ceil(MAX_LOGO_INVITACION / 3) * 4;

const MESES = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
];

/** '2026-11-15' → '15 de noviembre de 2026'. */
export function fechaLarga(dia: string): string {
  const [a, m, d] = dia.split('-').map(Number) as [number, number, number];
  return `${d} de ${MESES[m - 1] ?? ''} de ${a}`;
}

/**
 * "Condiciones de la prueba", verbatim from the playbook (text by
 * `abogado-cl`, 16-10-2026, condition 5 as adjusted by the owner). A test
 * compares it with docs/comercial/proceso-contactos.md: change both or none.
 */
export function condicionesPrueba(razonSocial: string, fechaFin: string): string {
  const rs = razonSocial;
  return [
    '**Condiciones de la prueba** (para cargar datos reales; responde este correo con la palabra «Acepto». El auto de ejemplo lo puedes mirar desde ya):',
    '',
    `1. ${rs} decide qué datos de sus clientes carga y para qué (nombre, RUT, teléfono, patente, fotos del vehículo y firma), y responde por haberlos obtenido de forma lícita y por informar a sus clientes.`,
    `2. Kalfun Tech SpA trata esos datos solo por encargo de ${rs} y para un fin: registrar y documentar las inspecciones y órdenes del taller en Tallerza. No los usa para ningún otro fin, no los cede a terceros y no los usa para fines propios.`,
    `3. ${rs} autoriza que los datos se alojen en Google Cloud/Firebase (Estados Unidos, región us-central1), proveedor de infraestructura de Kalfun.`,
    `4. Kalfun aplica medidas de seguridad razonables, guarda confidencialidad y avisará a ${rs} sin demora de cualquier vulneración de seguridad que afecte esos datos.`,
    `5. La prueba dura desde hoy hasta ${fechaFin}. Al terminar, si no se contrata, dentro de 15 días Kalfun entrega a ${rs} una exportación de sus datos, si la pide, y elimina los datos del taller. Las fotos y los PDF de las inspecciones quedan bloqueados por una retención técnica de integridad de hasta 1 año desde su captura, sin acceso de nadie (ni del taller ni de Kalfun, salvo requerimiento legal), y se eliminan al vencer ese plazo.`,
    '6. Estas condiciones se reemplazarán por los términos y el contrato de encargo definitivos cuando se publiquen.',
  ].join('\n');
}

export type DatosCorreoInvitacion = {
  /** Who wrote (the lead's name; empty for a waiting-list lead). */
  nombre: string;
  /** Commercial name of the workshop. */
  taller: string;
  correoAdmin: string;
  /** Up to three proposed slots for the guided demo (empty ones are skipped). */
  horarios?: string[];
};

export const asuntoInvitacion = (taller: string) => `Tu taller ya está en Tallerza: ${taller}`;

/**
 * Correo A of the playbook with every marker resolved except MARCA_CLAVE and
 * MARCA_CONDICIONES (the server fills both).
 */
export function cuerpoInvitacion(d: DatosCorreoInvitacion): string {
  const horarios = (d.horarios ?? []).map((h) => h.trim()).filter(Boolean);
  const saludo = d.nombre.trim() ? `Hola, ${d.nombre.trim()}:` : 'Hola:';
  const propuesta = horarios.length
    ? `Te proponemos:\n\n${horarios.map((h) => `- ${h}`).join('\n')}\n\nSi ninguno te acomoda, dinos cuál te sirve.`
    : 'Respóndenos con un día y una hora que te acomoden.';
  return `${saludo}

Gracias por escribirnos. Te dejamos dos formas de conocer Tallerza; puedes usar una o las dos.

**1. Pruébalo en tu propio taller.** Creamos ${d.taller} en Tallerza, con 1 auto de ejemplo marcado como ejemplo para que veas cómo se ve todo. Te adjuntamos un instructivo corto con los pasos (hacer un ingreso con fotos y firma, ver el PDF, mover el auto en el tablero y hacer la entrega).

- Backoffice (computador): https://admin.tallerza.cl
- App de inspección (teléfono o tablet): https://app.tallerza.cl
- App para Android, en Google Play: ${PLAY_URL}
- Correo: ${d.correoAdmin}
- Contraseña temporal: ${MARCA_CLAVE} (al entrar la primera vez te pedirá cambiarla)

${LINEA_IOS}

Al entrar, antes de tu primera inspección real, revisa en Configuración → Datos del taller → «Razón social y RUT» que la razón social y el RUT estén bien, porque salen en los PDF.

**Suma a tu equipo.** Con tu cuenta de administrador, crea a cada persona en el backoffice, en Usuarios → «Nueva persona»: cada una entra con su propio correo y contraseña, así que no compartas la tuya. Si varias personas usan una misma tablet, créala en Equipos compartidos. La guía completa, con video, está en https://tallerza.cl/docs.

Los accesos sirven en los dos sitios. La prueba dura 30 días y se puede hacer con autos reales: lo que cargues queda tuyo si sigues con Tallerza, sin traspasos ni migraciones. Para eso, te pedimos que quien representa al taller lea las condiciones de abajo y responda «Acepto» antes de cargar datos de tus clientes.

${MARCA_CONDICIONES}

**2. Te lo mostramos en 30 minutos.** Por videollamada (o en tu taller, si estás en Santiago). ${propuesta}

Y una pregunta para recomendarte el plan que te conviene: **¿cuántos autos entran a tu taller en un mes normal?**

Saludos,
Equipo Tallerza
contacto@tallerza.cl · WhatsApp +56 9 9487 6263`;
}

const contar = (texto: string, marca: string) => texto.split(marca).length - 1;

/** What is wrong with an edited body, or null. */
export function revisarCuerpoInvitacion(cuerpo: string): string | null {
  if (contar(cuerpo, MARCA_CLAVE) !== 1)
    return `El correo debe llevar ${MARCA_CLAVE} una vez: ahí va la contraseña temporal.`;
  if (contar(cuerpo, MARCA_CONDICIONES) !== 1)
    return `El correo debe llevar ${MARCA_CONDICIONES} una vez: ahí van las condiciones de la prueba.`;
  const otro = cuerpo
    .replaceAll(MARCA_CLAVE, '')
    .replaceAll(MARCA_CONDICIONES, '')
    .match(/\{\{[^}]*\}\}/);
  if (otro) return `Quedó un marcador sin completar: ${otro[0]}.`;
  return null;
}

/** The final text: the password and the conditions in place of their markers. */
export function resolverCuerpoInvitacion(
  cuerpo: string,
  d: { clave: string; razonSocial: string; fechaFin: string },
): string {
  return cuerpo
    .replace(MARCA_CONDICIONES, () => condicionesPrueba(d.razonSocial, fechaLarga(d.fechaFin)))
    .replace(MARCA_CLAVE, () => d.clave);
}

// ---- Callable contract (`invitarAPrueba`) ----

const idDoc = z.string().regex(/^[A-Za-z0-9_-]{1,128}$/);
export const ID_TALLER = /^[a-z0-9-]{3,40}$/;

const comun = {
  contactoId: idDoc,
  asunto: z.string().trim().min(3).max(200),
  cuerpo: z
    .string()
    .max(MAX_CUERPO_INVITACION)
    .superRefine((v, ctx) => {
      const error = revisarCuerpoInvitacion(v);
      if (error) ctx.addIssue({ code: 'custom', message: error });
    }),
};

/** Blank (or only spaces) is the same as absent; anything else must pass `e`. */
const opcional = <T extends z.ZodType>(e: T) =>
  z.preprocess((v) => (typeof v === 'string' && v.trim() === '' ? undefined : v), e.optional());

export const esquemaInvitarAPrueba = z.discriminatedUnion('modo', [
  z.strictObject({
    modo: z.literal('crear'),
    ...comun,
    tallerId: z.string().regex(ID_TALLER),
    nombre: z.string().trim().min(2).max(80),
    /**
     * Legal data is optional (a lead may not have it yet): the workshop admin
     * completes it later in Configuración. Empty counts as missing; a RUT that
     * comes is still checked.
     */
    razonSocial: opcional(z.string().trim().min(2).max(120)),
    rut: opcional(z.string().trim().max(20).refine(validarRut, 'RUT inválido')),
    direccion: opcional(z.string().trim().min(3).max(200)),
    adminNombre: z.string().trim().min(2).max(60),
    color: z.string().refine(esHexColor, 'Color #RRGGBB').optional(),
    /** PNG in base64 (no data: prefix), up to MAX_LOGO_INVITACION bytes. */
    logo: z
      .string()
      .max(MAX_LOGO_BASE64)
      .regex(/^[A-Za-z0-9+/]+={0,2}$/)
      .optional(),
    fechaFin: z.string().refine(esDiaIso, 'Fecha AAAA-MM-DD'),
  }),
  z.strictObject({
    /** New temporary password and the same e-mail (only while it was never changed). */
    modo: z.literal('reenviar'),
    ...comun,
    /** Only when the lead has no workshop yet (one created by hand). */
    tallerId: z.string().regex(ID_TALLER).optional(),
  }),
]);
export type InvitarAPrueba = z.infer<typeof esquemaInvitarAPrueba>;

/**
 * Answer of `invitarAPrueba`. `clave` only comes when the e-mail failed:
 * the superadmin sees it once and hands it over by hand (it is never stored).
 */
export type RespuestaInvitarAPrueba = {
  tallerId: string;
  correo: 'enviado' | 'fallido';
  clave?: string;
  /** Example car: created, failed (the workshop is fine without it) or not part of this call. */
  ejemplos: 'creados' | 'fallidos' | 'no_aplica';
};

/** `details` of the already-exists error: the lead (or the id) already has a workshop. */
export type DetalleYaInvitado = { motivo: 'ya_invitado' | 'taller_existe'; tallerId: string };

/** What the callable leaves on `contactos/{id}` (server fields). */
export type InvitacionContacto<F = unknown> = {
  tallerId: string;
  invitadoEn: F;
  /** uid of the superadmin. */
  invitadoPor: string;
  /** Last day of the trial ('AAAA-MM-DD'), as written in the e-mail. */
  pruebaHasta: string;
};
